import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Admin · Options authoring (spec 017 Feature A) — SUPER-ADMIN authoring UI for the
 * unified, admin-defined option groups and their choices. Two plain-language types:
 * "View options" (each pick renders a separate image) and "Prompt options" (prompt-only
 * guidance). Every choice carries an admin-only prompt (`harness_prompt`, never shown to
 * the customer) and an optional reference image.
 *
 * Reference-mock pass (Editor · Option groups): ONE frosted `.card` titled "Option groups"
 * with a sub-line, then a COMPACT ROW per group — group name + inline type pill (axis →
 * info-tinted / modifier → neutral) + a faint choices summary ("Back · Front · Floating") —
 * with an "Edit choices" secondary button on the trailing edge. "Edit choices" EXPANDS the
 * row inline into the full GroupCard editor (add/edit/delete choices + scope assignment), so
 * every option's parameters are still editable. "+ New option group" sits bottom-right and
 * reveals the create-group form. All choice-editing wiring + data-testids are preserved.
 */
import { useEffect, useState } from "react";
import { Accordion } from "../ui/Accordion.js";
import { ActionBar } from "../ui/ActionBar.js";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { InfoHint } from "../ui/Tooltip.js";
import type { OptionChoice, OptionGroup } from "@genz/platform";
import { CreateGroupForm } from "./CreateGroupForm.js";
import { GroupCard } from "./GroupCard.js";
import { KIND_LABELS, KIND_HINTS } from "./labels.js";
import "./options.css";

/** "Back · Front · Floating" — the first few live choice labels, middle-dot separated. */
function choicesSummary(choices: OptionChoice[]): string {
  return choices.map((c) => c.label).join(" · ");
}

export function OptionsPanel() {
  const { client } = useServices();
  const [groups, setGroups] = useState<OptionGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which group is expanded into its editor. Z0's fix round made `Accordion` controllable, so
  // this is the accordion's open key rather than a seed the accordion then owns: creating a
  // group sets it, and the row that vanishes (archived) simply stops matching.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  // Whether the "+ New option group" create form is revealed.
  const [creating, setCreating] = useState(false);

  async function refresh() {
    setError(null);
    try {
      setGroups((await client.adminListOptions()).groups);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setGroups([]);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const onGroupCreated = (g: OptionGroup) => {
    setGroups((prev) => [...(prev ?? []), { ...g, choices: g.choices ?? [] }]);
    setCreating(false);
    setOpenGroup(g.id); // open the new group straight into its editor
  };

  // Archived group → drop it; otherwise replace in place (keep loaded choices).
  const onGroupChanged = (u: OptionGroup) =>
    setGroups((prev) => (prev ?? []).flatMap((g) =>
      g.id !== u.id ? [g] : u.archived ? [] : [{ ...u, choices: u.choices ?? g.choices }]));

  // One handler for new + edited + archived choices, reconciled by id.
  const onChoiceChanged = (groupId: string, c: OptionChoice) =>
    setGroups((prev) => (prev ?? []).map((g) => {
      if (g.id !== groupId) return g;
      const exists = g.choices.some((x) => x.id === c.id);
      const choices = c.archived
        ? g.choices.filter((x) => x.id !== c.id)
        : exists
          ? g.choices.map((x) => (x.id === c.id ? c : x))
          : [...g.choices, c];
      return { ...g, choices };
    }));

  if (groups === null) {
    return <p className="faint" data-testid="options-loading">Loading options…</p>;
  }

  return (
    <section className="card options-panel" data-testid="options-panel">
      <div className="options-panel__head">
        <h2 className="panel-title">Option groups <InfoHint text="Customer-facing dropdowns. The trusted harness is composed server-side from the selected choice ids — no free-text prompt. View options each create a separate mock-up; Prompt options refine the prompt." /></h2>
      </div>

      {error && <p role="alert" data-testid="options-error" className="options-alert"><ErrorNotice detail={error} /></p>}

      {groups.length === 0 && !creating ? (
        <EmptyState icon="list" title="No option groups yet" hint="Create a group below to get started." testId="options-empty" />
      ) : (
        <div className="options-rows" data-testid="options-rows">
          {/* Rule 8. These rows were a hand-rolled accordion — `.options-row` +
              `.options-row--open` + an "Edit choices" toggle carrying `aria-expanded` — doing
              exactly what the shared primitive does, in a local vocabulary. They are
              `.accordion` items now. The `-edit-choices` testid follows the control it MERGED
              into: the accordion head IS the expander, and clicking it is what "Edit choices"
              did. `option-row-{id}` and `-choices` stay where they were.
              CONTROLLED (Z0 fix round F-6). This was re-keyed on the group list — `key={ids}` —
              so that a newly created group landed open, which remounted every OTHER row for a
              state change that had nothing to do with them. `openKey` moves the open section
              and leaves the rest of the list standing. The key is filtered through the live
              list so an archived group closes instead of leaving a dangling key. */}
          <Accordion
            name="admin-options"
            openKey={groups.some((g) => g.id === openGroup) ? openGroup : null}
            onOpenChange={setOpenGroup}
            items={groups.map((g) => ({
              key: g.id,
              /* The choices summary is part of the HEAD, not the meta slot (fix round 1,
                 L-4). `meta` is exempt from the toggle by design (F-7) because it is where a
                 real control goes — so with the summary in it, clicking the group name opened
                 the row and clicking the faint summary beside it did nothing. A read-only
                 summary is not a control; in the head the whole row is one target. */
              head: (
                <span className="options-row__name" data-testid={`option-row-${g.id}-edit-choices`}>
                  {g.label}
                  <span
                    className={`badge badge--plain ${g.kind === "axis" ? "badge--info" : "badge--neutral"}`}
                    title={KIND_HINTS[g.kind]}
                    data-testid={`option-row-${g.id}-kind`}
                  >
                    {KIND_LABELS[g.kind]}
                  </span>
                  <small className="options-row__choices" data-testid={`option-row-${g.id}-choices`}>
                    {g.choices.length === 0 ? "No choices yet" : choicesSummary(g.choices)}
                  </small>
                </span>
              ),
              body: (
                <div data-testid={`option-row-${g.id}`}>
                  <GroupCard
                    group={g}
                    onGroupChanged={onGroupChanged}
                    onChoiceCreated={(c) => onChoiceChanged(g.id, c)}
                    onError={setError}
                  />
                </div>
              ),
            }))}
          />
        </div>
      )}

      {creating ? (
        <CreateGroupForm
          // Append after the existing groups — the server sorts by `order`.
          nextOrder={groups.length ? Math.max(...groups.map((g) => g.order)) + 1 : 0}
          onCreated={onGroupCreated}
        />
      ) : (
        /* Rule 7: the panel's one invitation, in the bar at the bottom of the column. */
        <ActionBar
          label="Option group actions"
          primary={
            <button
              type="button"
              className="btn btn--primary"
              data-testid="option-new-group"
              onClick={() => setCreating(true)}
            >
              + New option group
            </button>
          }
        />
      )}
    </section>
  );
}
